import { useCallback, useState } from 'react';
import { captureSupabaseError } from '../lib/sentry-supabase';

// Click-to-reveal cell for an encrypted patient PHI field (name/phone).
//
// Renders as a bare "Show" affordance — the value is NOT present up
// front, and revealing FETCHES it from the server-side
// /api/patients/reveal route, which decrypts and logs the access. The
// browser never holds the ciphertext or the key; an unrevealed field's
// plaintext never crosses the wire. (Earlier iterations rendered a
// •••••• placeholder next to Show — dropped 2026-08-19 for a cleaner
// list at Dom's request; the semantic gap "there is a value here but
// you must click to see it" is already communicated by the button.)
//
// Once revealed it stays revealed for the row's lifetime (re-masking
// would only force a redundant re-fetch + re-log). Paginating remounts
// the cell with a fresh row id, so a new page starts masked again.

export type RevealField = 'full_name' | 'phone';

/** Resolves the decrypted value, or null when the field has no value. */
export type RevealFn = (
  patientId: string,
  field: RevealField,
) => Promise<string | null>;

type State = 'masked' | 'loading' | 'revealed' | 'error';

export function RevealablePhiCell({
  patientId,
  field,
  label,
  reveal,
}: {
  patientId: string;
  field: RevealField;
  /** Human label for the aria-label, e.g. "name" / "phone". */
  label: string;
  reveal: RevealFn;
}): JSX.Element {
  const [state, setState] = useState<State>('masked');
  const [value, setValue] = useState<string | null>(null);

  const onReveal = useCallback(async () => {
    if (state === 'loading' || state === 'revealed') return;
    setState('loading');
    try {
      const v = await reveal(patientId, field);
      setValue(v);
      setState('revealed');
    } catch (err) {
      captureSupabaseError(err, `patients.reveal.${field}`);
      setState('error');
    }
  }, [state, reveal, patientId, field]);

  if (state === 'revealed') {
    return <span className="text-ink">{value ?? '—'}</span>;
  }

  if (state === 'error') {
    return (
      <span className="inline-flex items-center gap-2">
        <span className="text-xs text-red-700">Couldn&apos;t reveal</span>
        <button
          type="button"
          onClick={() => void onReveal()}
          className="text-xs font-medium text-primary underline-offset-4 hover:underline"
        >
          Retry
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void onReveal()}
      disabled={state === 'loading'}
      aria-label={`Reveal ${label}`}
      className="text-xs font-medium text-primary underline-offset-4 hover:underline disabled:opacity-60"
    >
      {state === 'loading' ? 'Revealing…' : 'Show'}
    </button>
  );
}
